import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import clsx from "clsx";
import { useContext, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";

import BackdropLoading from "../components/BackdropLoading";
import GlobalSearch from "../components/GlobalSearch";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import { useThemeContext } from "../context/DarkMode";
import toastError from "../errors/toastError";
import api from "../services/api";
import { i18n } from "../translate/i18n";
import MainListItems from "./MainListItems";
import OfflineBanner from "../components/OfflineBanner";
import SocketStatusBanner from "../components/SocketStatusBanner";

const drawerWidth = 240;

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
  },

  // ── AppBar ───────────────────────────────────────────────────────────────
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: "none",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  toolbar: {
    minHeight: 48,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1),
    gap: theme.spacing(0.5),
  },
  menuButton: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flex: 1,
    minWidth: 0,
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: "-0.01em",
    color: theme.palette.primary.main,
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  userAvatar: {
    width: 30,
    height: 30,
    fontSize: 11,
    fontWeight: 700,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    border: `1.5px solid ${alpha(theme.palette.primary.main, 0.25)}`,
    cursor: "pointer",
    transition: "background-color 0.15s",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
  },
  iconButton: {
    color: theme.palette.text.secondary,
  },

  // ── Drawer ────────────────────────────────────────────────────────────────
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowX: "hidden",
    // Nav item pill style (Material You)
    "& .MuiListItemButton-root": {
      borderRadius: "0 24px 24px 0",
      marginRight: theme.spacing(1.5),
      transition: "background-color 0.15s",
    },
    "& .MuiListItemButton-root:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
    "& .MuiListItemButton-root.Mui-selected": {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      "& .MuiListItemIcon-root": { color: theme.palette.primary.main },
      "& .MuiListItemText-primary": {
        color: theme.palette.primary.main,
        fontWeight: 700,
      },
    },
    "& .MuiListItemIcon-root": {
      color: theme.palette.text.secondary,
      minWidth: 40,
    },
    "& .MuiListItemText-primary": {
      color: theme.palette.text.primary,
      fontSize: 13.5,
    },
    "& .MuiDivider-root": { borderColor: theme.palette.divider },
    "& .MuiListSubheader-root": {
      backgroundColor: "transparent",
      color: theme.palette.text.disabled,
      textTransform: "uppercase",
      fontSize: "0.68rem",
      fontWeight: 700,
      letterSpacing: "1.2px",
      lineHeight: "2.8",
    },
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
    // Icon-only mode: hide text and subheaders, center icons
    "& .MuiListItemText-root": { display: "none" },
    "& .MuiListSubheader-root": { display: "none" },
    "& .MuiListItemButton-root": {
      justifyContent: "center",
      paddingLeft: 0,
      paddingRight: 0,
      marginRight: 0,
      borderRadius: 0,
      minHeight: 44,
    },
    "& .MuiListItemIcon-root": {
      minWidth: 0,
      justifyContent: "center",
    },
  },

  // Drawer header row (brand + collapse toggle)
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    minHeight: 48,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    overflow: "hidden",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(0.5),
  },
  brandMark: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    flexShrink: 0,
  },
  brandName: {
    flex: 1,
    minWidth: 0,
    fontWeight: 700,
    fontSize: 14,
    color: theme.palette.primary.main,
    letterSpacing: "-0.01em",
    marginLeft: theme.spacing(1),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // ── Layout ────────────────────────────────────────────────────────────────
  appBarSpacer: { minHeight: 48 },
  content: { flex: 1, overflow: "auto" },
}));

const LoggedInLayout = ({ children }) => {
  const { classes } = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, trialActive, trialDaysLeft } =
    useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);

  const TRIAL_TOTAL_DAYS = 7;
  const trialColor =
    trialDaysLeft <= 1 ? "error" : trialDaysLeft <= 3 ? "warning" : "primary";
  const trialProgress = Math.round(
    ((TRIAL_TOTAL_DAYS - trialDaysLeft) / TRIAL_TOTAL_DAYS) * 100,
  );
  const { darkMode, toggleTheme } = useThemeContext();

  useEffect(() => {
    if (document.body.offsetWidth > 600) setDrawerOpen(true);
  }, []);

  useEffect(() => {
    setDrawerVariant(
      document.body.offsetWidth < 600 ? "temporary" : "permanent",
    );
  }, [drawerOpen]);

  const handleMenu = (e) => {
    setAnchorEl(e.currentTarget);
    setMenuOpen(true);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };
  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };
  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };
  const drawerClose = () => {
    if (document.body.offsetWidth < 600) setDrawerOpen(false);
  };

  if (loading) return <BackdropLoading />;

  return (
    <div className={classes.root}>
      {/* ── Sidebar ── */}
      <Drawer
        variant={drawerVariant}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose,
          ),
        }}
        open={drawerOpen}
      >
        {/* Brand header */}
        <Box
          className={classes.drawerHeader}
          sx={!drawerOpen ? { justifyContent: "center", pl: 0, pr: 0 } : {}}
        >
          {drawerOpen && (
            <>
              <Box className={classes.brandMark} />
              <Tooltip
                title={user.organizationName || ""}
                placement="right"
                arrow
                disableHoverListener={!user.organizationName}
              >
                <Typography className={classes.brandName}>
                  {user.organizationName || "Hashlix CRM"}
                </Typography>
              </Tooltip>
            </>
          )}
          <Tooltip
            title={drawerOpen ? "Collapse" : "Expand"}
            placement="right"
            arrow
          >
            <IconButton
              size="small"
              onClick={() => setDrawerOpen((v) => !v)}
              sx={{
                flexShrink: 0,
                transform: drawerOpen ? "none" : "rotate(180deg)",
                transition: "transform 0.22s",
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <List sx={{ pt: 0.5, flex: 1 }}>
          <MainListItems drawerClose={drawerClose} />
        </List>

        {/* ── Trial progress card ── */}
        {trialActive && (
          <>
            <Divider />
            {drawerOpen ? (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    Free Trial
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={`${trialColor}.main`}
                  >
                    {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} left
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={trialProgress}
                  color={trialColor}
                  sx={{
                    borderRadius: 4,
                    height: 6,
                    mb: 1.25,
                    bgcolor: "action.hover",
                  }}
                />
                <Button
                  variant="contained"
                  color={trialColor}
                  size="small"
                  fullWidth
                  disabled
                  sx={{
                    borderRadius: 2,
                    fontSize: "0.72rem",
                    textTransform: "none",
                  }}
                >
                  Upgrade Now
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1.25 }}>
                <Tooltip
                  title={`Trial — ${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""} left`}
                  placement="right"
                  arrow
                >
                  <HourglassBottomOutlinedIcon
                    fontSize="small"
                    color={trialColor}
                  />
                </Tooltip>
              </Box>
            )}
          </>
        )}
      </Drawer>

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      {/* ── Urgent trial strip (final day only) ── */}
      {trialActive && trialDaysLeft <= 1 && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          }}
        >
          <Alert
            severity="error"
            icon={false}
            sx={{ borderRadius: 0, justifyContent: "center", py: 0.5 }}
          >
            Your trial expires today — upgrade now to keep access.
          </Alert>
        </Box>
      )}

      {/* ── AppBar ── */}
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        sx={trialActive && trialDaysLeft <= 1 ? { top: 40 } : {}}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          {/* Hamburger — only visible when drawer is collapsed */}
          <IconButton
            edge="start"
            aria-label="open drawer"
            size="small"
            onClick={() => setDrawerOpen(true)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden,
            )}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          <Box sx={{ flex: 1, minWidth: 0, mx: 1 }}>
            <GlobalSearch />
          </Box>

          {/* Dark / light toggle */}
          <Tooltip title={darkMode ? "Light mode" : "Dark mode"} arrow>
            <IconButton
              size="small"
              onClick={toggleTheme}
              className={classes.actionBtn}
            >
              {darkMode ? (
                <LightModeOutlinedIcon sx={{ fontSize: 19 }} />
              ) : (
                <DarkModeOutlinedIcon sx={{ fontSize: 19 }} />
              )}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          {user.id && <NotificationsPopOver className={classes.iconButton} />}

          {/* User avatar menu */}
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Tooltip title={user.name || "Account"} arrow>
              <Avatar
                className={classes.userAvatar}
                onClick={handleMenu}
                aria-controls="menu-appbar"
                aria-haspopup="true"
              >
                {getInitials(user.name)}
              </Avatar>
            </Tooltip>
            {/* Online/offline status dot */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 9,
                height: 9,
                borderRadius: "50%",
                bgcolor: user.isOnline !== false ? "#22c55e" : "text.disabled",
                border: "1.5px solid",
                borderColor: "background.paper",
                pointerEvents: "none",
              }}
            />
          </Box>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={menuOpen}
            onClose={handleCloseMenu}
            slotProps={{
              paper: {
                elevation: 2,
                sx: { minWidth: 180, mt: 0.5, borderRadius: 2 },
              },
            }}
          >
            <MenuItem dense onClick={handleOpenUserModal}>
              {i18n.t("mainDrawer.appBar.user.profile")}
            </MenuItem>
            <MenuItem
              dense
              onClick={async () => {
                handleCloseMenu();
                try {
                  await api.put("/users/status", { isOnline: user.isOnline === false });
                } catch (err) {
                  toastError(err);
                }
              }}
            >
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: user.isOnline !== false ? "#22c55e" : "text.disabled",
                  mr: 1.25,
                  flexShrink: 0,
                }}
              />
              {user.isOnline !== false ? "Go Offline" : "Go Online"}
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              dense
              onClick={handleClickLogout}
              sx={{ color: "error.main" }}
            >
              {i18n.t("mainDrawer.appBar.user.logout")}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ── Page content ── */}
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <OfflineBanner />
        <SocketStatusBanner />
        {children ?? null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
